import React, { Component } from 'react';
/**
 * ExplanationCard — Teacher note aesthetic for explanations.
 *
 * Correct: warm amber "Waarom dit klopt" card.
 * Wrong: rose "Waarom was dat niet goed?" + amber teacher note.
 * Compact mode for inline usage.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { Lightbulb, CheckCircle2, Info, Zap, BookOpen } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { Card } from '../../../components/ui/Card';
import { ExplanationSteps } from './ExplanationSteps';
// ── Types ────────────────────────────────────────────────────
interface ExplanationCardProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationCard({
  explanation,
  distractorExplanation,
  isCorrect,
  mode = 'full',
  solutionSteps
}: ExplanationCardProps) {
  if (mode === 'compact') {
    return <div className="bg-amber-50 p-4 rounded-xl border border-amber-200" data-id="element-9960">
        <div className="flex items-center gap-2 font-bold text-amber-800 mb-2 text-sm" data-id="element-9961">
          <Zap className="w-4 h-4" data-id="element-9962" /> Uitleg
        </div>
        <div className="text-amber-900 text-sm leading-relaxed" data-id="element-9963">
          <LatexRenderer content={explanation} data-id="element-9964" />
        </div>
      </div>;
  }
  return <div className="space-y-5" data-id="element-9965">
      {/* Wrong answer — why was it wrong? */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-9966">
          <Card className="border-2 border-rose-100 bg-white overflow-hidden shadow-sm rounded-[2rem]" data-id="element-9967">
            <div className="bg-rose-50 px-6 py-4 border-b border-rose-100 flex items-center gap-3" data-id="element-9968">
              <div className="bg-white p-2 rounded-xl shadow-sm border border-rose-100 text-rose-500" data-id="element-9969">
                <Info className="w-5 h-5" data-id="element-9970" />
              </div>
              <h3 className="text-lg font-black text-rose-900" data-id="element-9971">
                Waarom was dat niet goed?
              </h3>
            </div>
            <div className="p-7 bg-rose-50/20" data-id="element-9972">
              <ExplanationSteps text={distractorExplanation} accent="indigo" data-id="element-9973" />
            </div>
          </Card>
        </motion.div>}

      {/* Teacher note — warm amber/yellow */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: !isCorrect && distractorExplanation ? 0.1 : 0,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-9974">
        <div className="relative rounded-[2rem] overflow-hidden border-2 border-amber-200 shadow-md" data-id="element-9975">
          <div className="h-1.5 w-full bg-amber-400" data-id="element-9976" />
          <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center gap-3" data-id="element-9977">
            <div className="bg-white p-2 rounded-xl shadow-sm border border-amber-200" data-id="element-9978">
              {isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-9979" /> : <Lightbulb className="w-5 h-5 text-amber-500 fill-amber-100" data-id="element-9980" />}
            </div>
            <div className="flex-1" data-id="element-9981">
              <h3 className="text-lg font-black text-amber-900" data-id="element-9982">
                {isCorrect ? 'Waarom dit klopt ✓' : 'Zo los je dit op'}
              </h3>
              <p className="text-xs text-amber-600 font-medium mt-0.5 flex items-center gap-1" data-id="element-9983">
                <BookOpen className="w-3 h-3" data-id="element-9984" /> Uitleg van de juf/meester
              </p>
            </div>
          </div>
          <div className="bg-amber-50/60 p-7" data-id="element-9985">
            <div className="relative" data-id="element-9986">
              <ExplanationSteps text={explanation || 'Goed gedaan! Je hebt de juiste strategie gebruikt.'} steps={solutionSteps} accent="amber" data-id="element-9987" />
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}